import React from 'react';
import { C } from '../../utils/styles';
import Icon from '../Icon';

/**
 * Lightweight modal-style breakdown sheet for KPI drill-downs.
 *
 * Renders an overlay with a right-side panel listing top contributors. Closing
 * uses the X button or backdrop click. Pure presentational — parent supplies
 * `items` already formatted.
 */
export default function MetricBreakdownSheet({ open, title, subtitle, items = [], valueFormatter, onClose, onItemClick }) {
  if (!open) return null;
  const fmt = valueFormatter || ((v) => v);

  return (
    <div
      data-testid="metric-breakdown-sheet"
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex', justifyContent: 'flex-end',
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(480px, 100vw)', height: '100vh',
          background: '#fff', boxShadow: '-8px 0 30px rgba(2,6,23,0.18)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ padding: '18px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 800, color: C.text, margin: 0 }}>{title}</h2>
            {subtitle && <p style={{ fontSize: 12, color: C.textMid, margin: '2px 0 0' }}>{subtitle}</p>}
          </div>
          <button
            type="button"
            data-testid="metric-breakdown-close"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: C.textMid, fontSize: 22, padding: 4, lineHeight: 1 }}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {items.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: C.textLight }}>
              <Icon name="folder-open-outline" style={{ fontSize: 30, display: 'block', margin: '0 auto 8px' }} />
              No data to break down for this view.
            </div>
          ) : (
            items.map((it, idx) => (
              <button
                key={`${it.id}-${idx}`}
                type="button"
                data-testid={`metric-breakdown-item-${idx}`}
                onClick={() => onItemClick && onItemClick(it)}
                style={{
                  width: '100%', display: 'grid', gridTemplateColumns: '32px 1fr auto',
                  gap: 12, alignItems: 'center',
                  padding: '10px 20px',
                  background: 'transparent', border: 'none', borderBottom: `1px solid ${C.borderLight}`,
                  textAlign: 'left', cursor: onItemClick ? 'pointer' : 'default',
                  fontFamily: 'inherit', transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => { if (onItemClick) e.currentTarget.style.background = C.bg; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span style={{
                  width: 28, height: 28, borderRadius: 8, background: C.bg, color: C.textMid,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 800, fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {idx + 1}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.label}
                  </div>
                  {it.subline && (
                    <div style={{ fontSize: 11, color: C.textMid, marginTop: 2 }}>{it.subline}</div>
                  )}
                </div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: C.text, fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(it.value)}
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
